import * as Sentry from "@sentry/react";
import {
  DEPLOYMENT_ENV,
  IS_DEPLOYMENT_ENV_PROD,
  IS_DEPLOYMENT_ENV_STAGING,
  IS_DEPLOYMENT_ENV_TEST,
  IS_LOCAL,
  IS_SENTRY_ENABLED,
  SUB_ENV,
} from "../envVariables";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { getAnalytics, logEvent } from "firebase/analytics";
import { User } from "../mobxStores/user/user";
import { UIData } from "../mobxStores/uiData/uiData";
import { Timezone } from "../mobxStores/userData/timezone";
import { Theme } from "../mobxStores/uiData/theme";
import { ConsoleContext } from "../mobxStores/consoleContext/consoleContext";

export const sentryMetadata = { initialized: false };

export async function setupSentry() {
  if (!IS_SENTRY_ENABLED) {
    return;
  }

  if (sentryMetadata.initialized) {
    console.log("Skipping sentry initialize, already initialized.");
    return;
  }
  console.log("Initializing sentry.");

  const environment = IS_LOCAL ? "local" : DEPLOYMENT_ENV;
  try {
    let deploymentEnv = environment;
    let sentryServiceUrl = "https://sentry.services.controlplane.site/dsn/key";
    if (IS_DEPLOYMENT_ENV_TEST) {
      deploymentEnv = "test";
    }
    if (IS_DEPLOYMENT_ENV_STAGING) {
      deploymentEnv = "staging";
    }
    if (IS_DEPLOYMENT_ENV_PROD) {
      deploymentEnv = "production";
    }
    let sub = "";
    if (SUB_ENV === "legacy") {
      sub = "legacy";
    }

    const keyRes = await axios.post(sentryServiceUrl, { environment: deploymentEnv, sub: sub });
    const key = keyRes.data.public;
    if (!key) {
      console.error("Failed to fetch sentry key for this version");
      return;
    }

    const CPLN_DEBUG = (localStorage.getItem("CPLN_DEBUG") || "") === "true";

    // @ts-ignore
    const about = require("./../about.json");
    Sentry.init({
      dsn: key,
      sampleRate: 1.0,
      attachStacktrace: true,
      debug: CPLN_DEBUG || !IS_DEPLOYMENT_ENV_PROD,
      release: about.version,
      maxBreadcrumbs: 30,
      normalizeDepth: 5,
      environment,
      beforeBreadcrumb(crumb) {
        if (crumb.category === "console") return null;
        if (crumb.category === "xhr" && crumb.data?.status_code < 400) return null;
        if (crumb.category === "xhr" && crumb.data?.status_code === 403) return null;
        if (crumb.category === "xhr" && crumb.data?.status_code === 404) return null;
        if (crumb.category === "ui.click") return null;
        if (crumb.category === "ui.input") return null;
        return crumb;
      },
      replaysSessionSampleRate: 0.01,
      replaysOnErrorSampleRate: 0.01,
      integrations: [
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
        }),
        // Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
      ],
      enableLogs: true,
    });
    sentryMetadata.initialized = true;

    const feedback = Sentry.feedbackIntegration({
      autoInject: false,
      showBranding: false,
      triggerLabel: "Feedback",
      triggerAriaLabel: "Suggest a Feature or Report a Bug",
      formTitle: "Send Feedback/Bug Report",
      submitButtonLabel: "Submit",
      messageLabel: "Message",
      messagePlaceholder: "Please describe the issue and what you expected to happen, or suggest a feature.",
      successMessageText:
        "We appreciate you taking the time to share your thoughts about your recent experience with Control Plane. Your opinion is very important to us, and we truly value your feedback.",
      showName: false,
      showEmail: false,
      onSubmitSuccess: (feedback) => {
        Sentry.captureMessage("User Feedback Submitted", { level: "info" });
        Sentry.setTag("sessionId", null);
      },
      onSubmitError: (error) => {
        Sentry.captureMessage("User Feedback Failed to Submit", { level: "error" });
        Sentry.setTag("sessionId", null);
      },
      onFormOpen: () => {
        const sessionId = uuidv4();
        Sentry.setTag("sessionId", sessionId);
      },
    });

    const feedbackElement = document.getElementById("feedback-button")!;
    if (!feedbackElement) {
      console.log("No feedback element");
    } else {
      const form = await feedback.createForm();
      form.appendToDom();
      feedbackElement.addEventListener("click", async () => {
        form.open();
        if (IS_DEPLOYMENT_ENV_PROD) {
          const analytics = getAnalytics(User.getFirebaseApp());
          logEvent(analytics, "feedback_button");
        }
      });
    }

    Sentry.addIntegration(feedback);
  } catch (e) {
    console.error("Failed to initialize Sentry", e.message);
  }
}

export function sentryLog(log: string, data: any = {}) {
  try {
    Sentry.logger.info(log, {
      "browser.tabId": UIData.tabId,
      "user.saml": User.isSaml,
      "timezone.iana": Intl.DateTimeFormat().resolvedOptions().timeZone,
      "timezone.offset": new Date().getTimezoneOffset(),
      "timezone.abbreviation": new Date().toLocaleTimeString("en-US", { timeZoneName: "short" }).split(" ")[2],
      "timezone.value": Timezone.value,
      "timezone.override": Timezone.override,
      "browser.theme": Theme.theme,
      "browser.os": getOS(),
      "context.org": ConsoleContext.org,
      "context.gvc": ConsoleContext.gvc,
      ...data,
    });
  } catch (e) {}
}

function getOS() {
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  const macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"];
  const windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"];
  const iosPlatforms = ["iPhone", "iPad", "iPod"];

  if (macosPlatforms.indexOf(platform) !== -1) {
    return "Mac OS";
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    return "iOS";
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    return "Windows";
  } else if (/Android/.test(userAgent)) {
    return "Android";
  } else if (/Linux/.test(platform)) {
    return "Linux";
  }

  return "Unknown OS";
}
